<template>
  <div v-if="show" class="setup-modal-overlay" @click.self="dismiss">
    <div class="setup-modal-card" :class="{ 'fade-in': show }">
      <!-- Close Button -->
      <button @click="dismiss" class="modal-close-btn" title="Close">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>

      <!-- Icon -->
      <div class="modal-icon">
        <img src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" alt="WhatsApp" class="whatsapp-logo" />
      </div>

      <!-- Content -->
      <h2 class="modal-title">Set Up WhatsApp Integration</h2>
      <p class="modal-description">
        Connect your WhatsApp Business account to QUALI-D to start managing customer conversations, automating responses, and tracking interactions—all from one platform.
      </p>

      <!-- Features List -->
      <div class="features-list">
        <div class="feature-item">
          <div class="feature-icon">💬</div>
          <div class="feature-text">
            <strong>Unified Inbox</strong>
            <span>Manage all WhatsApp conversations in one place</span>
          </div>
        </div>
        <div class="feature-item">
          <div class="feature-icon">🤖</div>
          <div class="feature-text">
            <strong>AI-Powered Responses</strong>
            <span>Automate replies and save time</span>
          </div>
        </div>
        <div class="feature-item">
          <div class="feature-icon">📊</div>
          <div class="feature-text">
            <strong>Analytics & Insights</strong>
            <span>Track performance and customer engagement</span>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="modal-actions">
        <button @click="startSetup" class="btn-primary">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12,2L1,21H23M12,6L19.53,19H4.47M11,10V14H13V10M11,16V18H13V16"/>
          </svg>
          <span>Start Setup Tutorial</span>
        </button>
        <button @click="dismiss" class="btn-secondary">
          Maybe Later
        </button>
      </div>

      <!-- Setup Time Estimate -->
      <p class="setup-time">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22C6.47,22 2,17.5 2,12A10,10 0 0,1 12,2M12.5,7V12.25L17,14.92L16.25,16.15L11,13V7H12.5Z"/>
        </svg>
        Setup takes approximately 10-15 minutes
      </p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'WhatsAppSetupModal',
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    startSetup () {
      this.$emit('start-setup')
    },
    dismiss () {
      this.$emit('dismiss')
    }
  }
}
</script>

<style scoped>
.setup-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.setup-modal-card {
  position: relative;
  max-width: 600px;
  width: 100%;
  background: var(--bg-primary, #ffffff);
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.4s ease;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-close-btn {
  position: absolute;
  top: 20px;
  right: 20px;
  padding: 8px;
  background: transparent;
  border: none;
  color: var(--text-secondary, #6b7280);
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-close-btn:hover {
  background: var(--bg-secondary, #f3f4f6);
  color: var(--text-primary, #111827);
}

.modal-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  margin: 0 auto 24px;
  background: linear-gradient(135deg, #25d366 0%, #128c7e 100%);
  border-radius: 20px;
  box-shadow: 0 8px 24px rgba(37, 211, 102, 0.3);
}

.whatsapp-logo {
  width: 48px;
  height: 48px;
  filter: brightness(0) invert(1);
}

.modal-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary, #111827);
  text-align: center;
  margin: 0 0 12px 0;
  line-height: 1.3;
}

.modal-description {
  font-size: 16px;
  color: var(--text-secondary, #6b7280);
  text-align: center;
  margin: 0 0 32px 0;
  line-height: 1.6;
}

.features-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 32px;
}

.feature-item {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px;
  background: var(--bg-secondary, #f9fafb);
  border-radius: 12px;
  transition: all 0.2s;
}

.feature-item:hover {
  background: var(--bg-tertiary, #f3f4f6);
  transform: translateX(4px);
}

.feature-icon {
  font-size: 28px;
  flex-shrink: 0;
}

.feature-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.feature-text strong {
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary, #111827);
}

.feature-text span {
  font-size: 14px;
  color: var(--text-secondary, #6b7280);
}

.modal-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
}

.btn-primary,
.btn-secondary {
  width: 100%;
  padding: 14px 24px;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.btn-primary {
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
}

.btn-secondary {
  background: transparent;
  color: var(--text-secondary, #6b7280);
  border: 2px solid var(--border-primary, #e5e7eb);
}

.btn-secondary:hover {
  background: var(--bg-secondary, #f3f4f6);
  color: var(--text-primary, #111827);
  border-color: var(--border-secondary, #d1d5db);
}

.setup-time {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  color: var(--text-secondary, #6b7280);
  margin: 0;
}

.setup-time svg {
  opacity: 0.7;
}

/* Dark Mode Support */
@media (prefers-color-scheme: dark) {
  .setup-modal-card {
    --bg-primary: #1f2937;
    --bg-secondary: #374151;
    --bg-tertiary: #4b5563;
    --text-primary: #f9fafb;
    --text-secondary: #d1d5db;
    --border-primary: #4b5563;
    --border-secondary: #6b7280;
  }
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .setup-modal-card {
    padding: 30px 24px;
  }

  .modal-title {
    font-size: 24px;
  }

  .modal-description {
    font-size: 15px;
  }

  .feature-icon {
    font-size: 24px;
  }

  .feature-text strong {
    font-size: 14px;
  }

  .feature-text span {
    font-size: 13px;
  }
}
</style>
